import React, { createContext, useMemo, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import usePaymentErrors from './hooks/usePaymentErrors';
import usePaymentTokens from './hooks/usePaymentTokens';
import usePaymentStage from './hooks/usePaymentStage';
import usePaymentMethodChange from './hooks/usePaymentMethodChange';
import resolveFlexBillingFrequency from '../../../helpers/flex-helpers/flexBillingFrequency';

const BILLING_FREQUENCY_BY_TERM = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual',
  ANNUALLY: 'annual',
};

const resolveIsTrial = ({ isFlexFlow, isTrial, upgradePlan }) => {
  if (isFlexFlow) {
    return (upgradePlan?.product?.trialLength ?? 0) > 0;
  }

  return Boolean(isTrial);
};

const resolveUpgradeOffer = ({ upgradeOffer, upgradePlan, isFlexFlow }) => {
  if (!isFlexFlow) {
    return upgradeOffer;
  }

  if (!upgradePlan?.product) {
    return null;
  }

  const { product } = upgradePlan;
  const billingFrequency = BILLING_FREQUENCY_BY_TERM[product.term] ?? product.term?.toLowerCase?.() ?? null;

  return {
    id: product.planCode,
    planCode: product.planCode,
    family: upgradePlan.family,
    name: product.name,
    descriptors: [],
    terms: [
      {
        price: product.price,
        billingFrequency,
      },
    ],
  };
};

const resolveCurrentSubscription = ({ currentFlexSubscription, isFlexFlow }) => {
  if (!isFlexFlow) {
    return null;
  }

  return {
    family: currentFlexSubscription?.family ?? null,
    planCode: currentFlexSubscription?.planCode ?? null,
    freeTrial: currentFlexSubscription?.freeTrial === true,
    billingFrequency:
      currentFlexSubscription?.billingFrequency ??
      resolveFlexBillingFrequency({
        term: currentFlexSubscription?.termFrequency,
        planCode: currentFlexSubscription?.planCode,
      }),
    ...(typeof currentFlexSubscription?.price === 'number' ? { price: currentFlexSubscription.price } : {}),
  };
};

export const PAYMENT_STAGES = Object.freeze({
  REQUIRES_PAYMENT_METHOD: 0,
  REQUIRES_AUTHORIZATION: 1,
  REQUIRES_FULFILLMENT: 2,
  PAYMENT_METHOD_UPDATED: 3,
  SERVICE_UNAVAILABLE: 4,
  CHOOSE_BILLING_DATE: 5,
  REVIEW_AND_PAY: 6,
  CHANGE_BILLING_DATE_PROCESSING: 7,
  CHANGE_BILLING_DATE_SUCCESS: 8,
  CHANGE_BILLING_DATE_FAILURE: 9,
  CHANGE_BILLING_DATE_LOADING: 10,
});

export const DEFAULT_TOKENS_STATE = Object.freeze({
  paymentMethod: null,
  authorizationInput: null,
  authorizationOutput: null,
  is3dsInitiatedOnEnforcedVerification: false,
  isUsingExistingMethod: false,
  flexUserMigrationFailed: false,
});

export const PaymentContext = createContext();

/* 
  PaymentContextProvider holds the state of the payment journey but does not handle any logic on when we should move
  between stages or trigger side effects. State is accessible via PaymentContext in the stages themselves.
  Please be mindful of what state is added to the context - the values stored here should be required between multiple stages,
  if it's only required in one stage it should be stored in that stage's state.
*/
const PaymentContextProvider = ({
  upgradeOffer,
  upgradePlan,
  currentFlexSubscription,
  isTrial,
  isFlexFlow,
  isWebview,
  isMethodChange,
  isBillingPeriodChange,
  targetBillingPeriod,
  isRestart,
  initialPaymentStage,
  children,
}) => {
  const { paymentErrors, raisePaymentError, clearPaymentErrors } = usePaymentErrors();
  const { tokens, addPaymentToken, setTokens } = usePaymentTokens();
  const resolvedInitialStage = initialPaymentStage ?? PAYMENT_STAGES.REQUIRES_PAYMENT_METHOD;
  const { paymentStage, goToPaymentStage } = usePaymentStage(resolvedInitialStage);

  const { paymentChangeActive, setPaymentChangeActive, paymentChangeType, setPaymentChangeType } =
    usePaymentMethodChange();
  const resolvedUpgradeOffer = resolveUpgradeOffer({ upgradeOffer, upgradePlan, isFlexFlow });
  // Keep the plan price from the start of the upsell. A later entitlements refresh can already be the new plan.
  const capturedPlanPriceRef = useRef(null);
  if (capturedPlanPriceRef.current == null && typeof currentFlexSubscription?.price === 'number') {
    capturedPlanPriceRef.current = currentFlexSubscription.price;
  }
  const subscriptionForResolution =
    capturedPlanPriceRef.current == null
      ? currentFlexSubscription
      : { ...currentFlexSubscription, price: capturedPlanPriceRef.current };
  const resolvedCurrentSubscription = resolveCurrentSubscription({
    currentFlexSubscription: subscriptionForResolution,
    isFlexFlow,
  });
  const resolvedIsTrial = resolveIsTrial({ isFlexFlow, isTrial, upgradePlan });

  const [selectedPlanOverride, setSelectedPlanOverride] = useState(null);

  const restartPaymentFlow = useCallback(() => {
    setTokens(DEFAULT_TOKENS_STATE);
    goToPaymentStage(resolvedInitialStage);
  }, [setTokens, goToPaymentStage, resolvedInitialStage]);

  const contextValue = useMemo(
    () => ({
      upgradeOffer,
      upgradePlan,
      currentFlexSubscription,
      resolvedUpgradeOffer,
      resolvedCurrentSubscription,
      isTrial,
      resolvedIsTrial,
      isFlexFlow,
      isWebview,
      isMethodChange,
      isBillingPeriodChange,
      targetBillingPeriod,
      isRestart,
      paymentErrors,
      raisePaymentError,
      clearPaymentErrors,
      restartPaymentFlow,
      paymentStage,
      goToPaymentStage,
      tokens,
      addPaymentToken,
      paymentChangeActive,
      setPaymentChangeActive,
      paymentChangeType,
      setPaymentChangeType,
      selectedPlanOverride,
      setSelectedPlanOverride,
    }),
    [
      upgradeOffer,
      upgradePlan,
      currentFlexSubscription,
      resolvedUpgradeOffer,
      resolvedCurrentSubscription,
      isTrial,
      resolvedIsTrial,
      isFlexFlow,
      isWebview,
      isMethodChange,
      isBillingPeriodChange,
      targetBillingPeriod,
      isRestart,
      paymentErrors,
      raisePaymentError,
      clearPaymentErrors,
      restartPaymentFlow,
      paymentStage,
      goToPaymentStage,
      tokens,
      addPaymentToken,
      paymentChangeActive,
      setPaymentChangeActive,
      paymentChangeType,
      setPaymentChangeType,
      selectedPlanOverride,
      setSelectedPlanOverride,
    ]
  );

  return <PaymentContext.Provider value={contextValue}>{children}</PaymentContext.Provider>;
};

PaymentContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  upgradeOffer: PropTypes.shape({
    family: PropTypes.string,
    name: PropTypes.string,
    upc: PropTypes.string,
    id: PropTypes.string,
    descriptors: PropTypes.arrayOf(PropTypes.string),
  }),
  upgradePlan: PropTypes.shape({
    family: PropTypes.string,
    product: PropTypes.shape({
      planCode: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      trialLength: PropTypes.number,
      term: PropTypes.string,
    }),
  }),
  currentFlexSubscription: PropTypes.shape({
    family: PropTypes.string,
    planCode: PropTypes.string,
    freeTrial: PropTypes.bool,
    termFrequency: PropTypes.string,
    billingFrequency: PropTypes.string,
    price: PropTypes.number,
  }),
  isTrial: PropTypes.bool,
  isFlexFlow: PropTypes.bool,
  isWebview: PropTypes.bool,
  isMethodChange: PropTypes.bool,
  isBillingPeriodChange: PropTypes.bool,
  targetBillingPeriod: PropTypes.oneOf(['monthly', 'annual']),
  isRestart: PropTypes.bool,
  initialPaymentStage: PropTypes.number,
};

PaymentContextProvider.defaultProps = {
  upgradeOffer: null,
  upgradePlan: null,
  currentFlexSubscription: null,
  isTrial: false,
  isFlexFlow: false,
  isWebview: false,
  isMethodChange: false,
  isBillingPeriodChange: false,
  targetBillingPeriod: undefined,
  isRestart: false,
  initialPaymentStage: undefined,
};

export default PaymentContextProvider;
